// ── Lista de Vidas: controle de adicionais (núcleo puro, F1) ──────
// O CRM passa a controlar, por vaga, o Odonto (Clin) e o Psicólogo (Zenklub),
// e, por empresa, a bonificação de cada adicional (inclusive o TotalPass, que
// é da empresa e só se lê do Hub). Na F1 nada é escrito no Hub e nada muda no
// boleto: o controle vive em lista_vidas.adicionais_vida / adicionais_empresa.
//
// Sem I/O, sem React, sem alias `@`, sem `Date.now()`: o "hoje" entra por
// parâmetro. O servidor (CommonJS) espelha TUDO o que é exportado aqui em
// server/lista-vidas/adicionais.js, com os mesmos nomes; a paridade é
// conferida por paridadeServidor.test.ts. Mudou aqui, muda lá.
//
// Onde mora cada regra:
//   - status efetivo, divergência com o Hub, transições permitidas, validação
//     da liberação e da bonificação, meses isentos e contagens: AQUI;
//   - papel ('cs' | 'admin'), CAS de versão, "liberar só a partir de
//     contratado" na gravação e histórico append-only: RPCs do banco
//     (lista_vidas.salvar_adicional_vida / liberar_zenklub / salvar_bonificacao);
//   - leitura fresca do Hub, ocupanteRef (HMAC do e-mail) e o envelope HTTP:
//     server/lista-vidas/servico.js.
//
// Máquina de estados por vaga (adicional zenklub_psi; clin_odonto não tem 'ativo'):
//   nao_contratado ⇄ contratado            (salvar-adicional-vida)
//   contratado     → ativo                 (liberar-zenklub, só zenklub_psi)
//   ativo          → contratado | nao_contratado (salvar-adicional-vida; zera a liberação)
//   qualquer       → ativo por salvar      PROIBIDO (transicao_invalida)

import type { VidaHub } from './vidaHubModel';
import { estaCancelada, normalizarData, temPessoa } from './vidaHubModel';

// ── Domínios ──────────────────────────────────────────────────────

export type AdicionalVida = 'clin_odonto' | 'zenklub_psi';
export type AdicionalEmpresa = AdicionalVida | 'totalpass';
export const ADICIONAIS_VIDA: readonly AdicionalVida[] = ['clin_odonto', 'zenklub_psi'];
export const ADICIONAIS_EMPRESA: readonly AdicionalEmpresa[] = ['clin_odonto', 'zenklub_psi', 'totalpass'];
export type StatusAdicional = 'contratado' | 'ativo' | 'nao_contratado';
export const STATUS_POR_ADICIONAL: Record<AdicionalVida, readonly StatusAdicional[]> = {
  clin_odonto: ['contratado', 'nao_contratado'],
  zenklub_psi: ['contratado', 'ativo', 'nao_contratado'],
};
export const ADICIONAL_LABELS: Record<AdicionalEmpresa, string> = {
  clin_odonto: 'Odonto (Clin)', zenklub_psi: 'Psicólogo (Zenklub)', totalpass: 'TotalPass',
};
export const STATUS_ADICIONAL_LABELS: Record<StatusAdicional, string> = {
  contratado: 'Contratado', ativo: 'Ativo', nao_contratado: 'Não contratado',
};

/** Linha gravada no CRM (lista_vidas.adicionais_vida), já em camelCase. */
export interface LinhaAdicionalVida {
  adicional: AdicionalVida;
  status: StatusAdicional;
  ocupanteRef: string | null;
  versao: number;
  pedidoRhEm: string | null;
  gestorRhNome: string | null;
  liberadoPor: string | null;
  liberadoEm: string | null;
}
export type OrigemControle = 'crm' | 'hub';
export type DivergenciaAdicional = 'hub_marca' | 'hub_nao_marca' | 'ocupante_mudou';
export interface LiberacaoZenklub { pedidoEm: string; gestorRhNome: string; liberadoEm: string; liberadoPor: string }
export interface ControleAdicional {
  adicional: AdicionalVida;
  status: StatusAdicional;
  origem: OrigemControle;
  /** null = ainda não há linha no CRM. */
  versao: number | null;
  divergencia: DivergenciaAdicional | null;
  /** Só com status 'ativo'. */
  liberacao: LiberacaoZenklub | null;
}

export type CodigoErroAdicional =
  | 'adicional_invalido' | 'status_invalido' | 'sem_alteracao' | 'transicao_invalida'
  | 'vida_nao_encontrada' | 'vaga_cancelada' | 'sem_pessoa' | 'sem_email'
  | 'data_invalida' | 'datas_invertidas' | 'data_futura' | 'nome_invalido'
  | 'bonificacao_sem_contrato';
/** Todos os códigos, na ordem do tipo (para a tela conferir que tem mensagem para cada um). */
export const CODIGOS_ERRO_ADICIONAL: readonly CodigoErroAdicional[] = [
  'adicional_invalido', 'status_invalido', 'sem_alteracao', 'transicao_invalida',
  'vida_nao_encontrada', 'vaga_cancelada', 'sem_pessoa', 'sem_email',
  'data_invalida', 'datas_invertidas', 'data_futura', 'nome_invalido',
  'bonificacao_sem_contrato',
];
/** Nunca traz valor (nome do gestor, e-mail, datas): só o código e o nome do campo. */
export interface ErroAdicional { codigo: CodigoErroAdicional; campo?: string }

export interface BonificacaoEmpresa {
  adicional: AdicionalEmpresa;
  bonificado: boolean;
  inicio: string | null;
  fim: string | null;
  versao: number | null;
}

export interface ResumoAdicionais {
  totalpass: { vidas: number };
  clin_odonto: { contratadas: number };
  /** contratadas = contratado + ativo. */
  zenklub_psi: { contratadas: number; ativos: number };
}

const NOME_GESTOR_MIN = 3;
const NOME_GESTOR_MAX = 120;

// ── Auxiliares ────────────────────────────────────────────────────

const ehAdicionalVida = (x: unknown): x is AdicionalVida =>
  typeof x === 'string' && (ADICIONAIS_VIDA as readonly string[]).includes(x);

const ehAdicionalEmpresa = (x: unknown): x is AdicionalEmpresa =>
  typeof x === 'string' && (ADICIONAIS_EMPRESA as readonly string[]).includes(x);

/** O Hub marca o adicional nesta vaga (código exato em `vida.adicionais`). */
const hubMarca = (vida: VidaHub, adicional: AdicionalEmpresa): boolean =>
  Array.isArray(vida.adicionais) && vida.adicionais.includes(adicional);

/** Vazio = undefined, null ou texto só de espaços. */
const vazio = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/** Texto (só string), NFC, sem espaço nas pontas, espaços colapsados. */
const colapsar = (v: unknown): string =>
  (typeof v === 'string' ? v.normalize('NFC').trim().replace(/\s+/g, ' ') : '');

/** Tamanho em pontos de código (o `char_length` do Postgres). */
const tamanho = (s: string): number => Array.from(s).length;

const RE_COMPETENCIA = /^\d{4}-(0[1-9]|1[0-2])$/;

/** `hojeIso` inválido é erro de programação (sem relógio não se prova "não futuro"). */
function exigirHoje(hojeIso: unknown, quem: string): string {
  const h = normalizarData(hojeIso);
  if (!h) throw new TypeError(`${quem}: hojeIso inválido.`);
  return h;
}

// ── Elegibilidade e status efetivo ────────────────────────────────

/** Vaga elegível a controle por vida: não cancelada e com pessoa. */
export function vagaElegivel(vida: VidaHub): boolean {
  return !estaCancelada(vida) && temPessoa(vida);
}

/** Divergência entre o status efetivo e o que o Hub marca (fora 'ocupante_mudou'). */
function divergenciaComHub(status: StatusAdicional, marca: boolean): DivergenciaAdicional | null {
  if (status === 'nao_contratado' && marca) return 'hub_marca';
  if (status !== 'nao_contratado' && !marca) return 'hub_nao_marca';
  return null;
}

function liberacaoDaLinha(linha: LinhaAdicionalVida): LiberacaoZenklub | null {
  const { pedidoRhEm, gestorRhNome, liberadoEm, liberadoPor } = linha;
  if (!pedidoRhEm || !gestorRhNome || !liberadoEm || !liberadoPor) return null;
  return { pedidoEm: pedidoRhEm, gestorRhNome, liberadoEm, liberadoPor };
}

/**
 * Status efetivo de um adicional numa vaga.
 *  - linha do CRM do mesmo ocupante (ou sem ocupante gravado): vale a linha (origem 'crm');
 *  - linha de OUTRO ocupante: vale o Hub, com a versão da linha (para o CAS) e
 *    divergência 'ocupante_mudou';
 *  - sem linha: vale o Hub ('contratado' se marca, senão 'nao_contratado'), versão null.
 * Divergência (fora 'ocupante_mudou'): 'hub_marca' quando o efetivo é
 * 'nao_contratado' e o Hub marca; 'hub_nao_marca' quando é 'contratado'/'ativo'
 * e o Hub não marca. `liberacao` só com status 'ativo'.
 */
export function efetivoAdicional(
  vida: VidaHub,
  adicional: AdicionalVida,
  linha: LinhaAdicionalVida | null,
  ocupanteRefAtual: string | null,
): ControleAdicional {
  const marca = hubMarca(vida, adicional);
  const doHub: StatusAdicional = marca ? 'contratado' : 'nao_contratado';
  if (!linha) {
    return {
      adicional, status: doHub, origem: 'hub', versao: null,
      divergencia: divergenciaComHub(doHub, marca), liberacao: null,
    };
  }
  if (linha.ocupanteRef !== null && linha.ocupanteRef !== ocupanteRefAtual) {
    return {
      adicional, status: doHub, origem: 'hub', versao: linha.versao,
      divergencia: 'ocupante_mudou', liberacao: null,
    };
  }
  return {
    adicional, status: linha.status, origem: 'crm', versao: linha.versao,
    divergencia: divergenciaComHub(linha.status, marca),
    liberacao: linha.status === 'ativo' ? liberacaoDaLinha(linha) : null,
  };
}

// ── Transições ────────────────────────────────────────────────────

/**
 * Mudança por salvar-adicional-vida (nunca para 'ativo'). null = pode.
 * Ordem: adicional_invalido; status fora de STATUS_POR_ADICIONAL[adicional] →
 * status_invalido; novo 'ativo' → transicao_invalida; novo === atual →
 * sem_alteracao. O resto é permitido (inclui ativo→contratado e
 * ativo→nao_contratado, que zeram a liberação no banco).
 */
export function validarMudancaStatus(adicional: AdicionalVida, atual: StatusAdicional, novo: string): ErroAdicional | null {
  if (!ehAdicionalVida(adicional)) return { codigo: 'adicional_invalido', campo: 'adicional' };
  if (typeof novo !== 'string' || !STATUS_POR_ADICIONAL[adicional].includes(novo as StatusAdicional)) {
    return { codigo: 'status_invalido', campo: 'status' };
  }
  if (novo === 'ativo') return { codigo: 'transicao_invalida', campo: 'status' };
  if (novo === atual) return { codigo: 'sem_alteracao', campo: 'status' };
  return null;
}

/** Uma data da liberação: '' com o erro já empurrado, ou a data. */
function checarDataLiberacao(bruto: unknown, campo: string, hoje: string, erros: ErroAdicional[]): string {
  const d = normalizarData(bruto);
  if (!d) { erros.push({ codigo: 'data_invalida', campo }); return ''; }
  if (d > hoje) { erros.push({ codigo: 'data_futura', campo }); return ''; }
  return d;
}

/**
 * Liberação do Zenklub (contratado → ativo). Devolve todos os erros de uma vez,
 * nesta ordem: transicao_invalida (status atual não é 'contratado'), sem_email,
 * pedidoEm (data_invalida | data_futura), liberadoEm (idem; ausente = hoje),
 * datas_invertidas (pedido depois da liberação), gestorRhNome (nome_invalido:
 * 3 a 120 caracteres depois de colapsar os espaços). `hojeIso` inválido lança TypeError.
 */
export function validarLiberacao(e: {
  statusAtual: StatusAdicional; temEmail: boolean; pedidoEm: unknown;
  gestorRhNome: unknown; liberadoEm?: unknown; hojeIso: string;
}):
  | { ok: true; valores: { pedidoEm: string; gestorRhNome: string; liberadoEm: string } }
  | { ok: false; erros: ErroAdicional[] } {
  const hoje = exigirHoje(e.hojeIso, 'validarLiberacao');
  const erros: ErroAdicional[] = [];
  if (e.statusAtual !== 'contratado') erros.push({ codigo: 'transicao_invalida' });
  if (e.temEmail !== true) erros.push({ codigo: 'sem_email' });

  const pedidoEm = checarDataLiberacao(e.pedidoEm, 'pedidoEm', hoje, erros);
  const liberadoEm = vazio(e.liberadoEm) ? hoje : checarDataLiberacao(e.liberadoEm, 'liberadoEm', hoje, erros);
  if (pedidoEm && liberadoEm && pedidoEm > liberadoEm) erros.push({ codigo: 'datas_invertidas', campo: 'pedidoEm' });

  const gestorRhNome = colapsar(e.gestorRhNome);
  const n = tamanho(gestorRhNome);
  if (n < NOME_GESTOR_MIN || n > NOME_GESTOR_MAX) erros.push({ codigo: 'nome_invalido', campo: 'gestorRhNome' });

  if (erros.length) return { ok: false, erros };
  return { ok: true, valores: { pedidoEm, gestorRhNome, liberadoEm } };
}

// ── Bonificação da empresa ────────────────────────────────────────

/**
 * Bonificação de um adicional da empresa. Todos os erros de uma vez:
 * adicional_invalido; `bonificado` que não é boolean → status_invalido
 * (campo 'bonificado'); com bonificado true: inicio/fim AAAA-MM-DD reais
 * (data_invalida), fim antes do início (datas_invertidas, campo 'fim') e
 * `contratadas` <= 0 (bonificacao_sem_contrato). Com bonificado false, início
 * e fim são ignorados e voltam null (desligar sempre pode). Início no passado
 * é permitido.
 */
export function validarBonificacao(e: {
  adicional: AdicionalEmpresa; bonificado: unknown; inicio?: unknown; fim?: unknown; contratadas: number;
}):
  | { ok: true; valores: { bonificado: boolean; inicio: string | null; fim: string | null } }
  | { ok: false; erros: ErroAdicional[] } {
  const erros: ErroAdicional[] = [];
  if (!ehAdicionalEmpresa(e.adicional)) erros.push({ codigo: 'adicional_invalido', campo: 'adicional' });
  if (typeof e.bonificado !== 'boolean') {
    erros.push({ codigo: 'status_invalido', campo: 'bonificado' });
    return { ok: false, erros };
  }
  if (!e.bonificado) {
    return erros.length ? { ok: false, erros } : { ok: true, valores: { bonificado: false, inicio: null, fim: null } };
  }
  const inicio = normalizarData(e.inicio);
  const fim = normalizarData(e.fim);
  if (!inicio) erros.push({ codigo: 'data_invalida', campo: 'inicio' });
  if (!fim) erros.push({ codigo: 'data_invalida', campo: 'fim' });
  if (inicio && fim && fim < inicio) erros.push({ codigo: 'datas_invertidas', campo: 'fim' });
  const contratadas = typeof e.contratadas === 'number' && Number.isFinite(e.contratadas) ? e.contratadas : 0;
  if (contratadas <= 0) erros.push({ codigo: 'bonificacao_sem_contrato' });
  if (erros.length) return { ok: false, erros };
  return { ok: true, valores: { bonificado: true, inicio, fim } };
}

/** Índice do mês (ano*12 + mês-1) de uma data AAAA-MM-DD já validada ou de uma competência AAAA-MM. */
const indiceMes = (s: string): number => Number(s.slice(0, 4)) * 12 + Number(s.slice(5, 7)) - 1;

/** Quantidade de meses (competências) tocados de `inicio` a `fim`, inclusive. Datas inválidas ou invertidas → 0. */
export function mesesTocados(inicio: string, fim: string): number {
  const i = normalizarData(inicio);
  const f = normalizarData(fim);
  if (!i || !f || f < i) return 0;
  return indiceMes(f) - indiceMes(i) + 1;
}

/** A competência 'AAAA-MM' é isenta: bonificado e mês(inicio) ≤ competência ≤ mês(fim). */
export function competenciaIsenta(
  b: Pick<BonificacaoEmpresa, 'bonificado' | 'inicio' | 'fim'>,
  competencia: string,
): boolean {
  if (!b || b.bonificado !== true) return false;
  if (typeof competencia !== 'string' || !RE_COMPETENCIA.test(competencia)) return false;
  const i = normalizarData(b.inicio);
  const f = normalizarData(b.fim);
  if (!i || !f || f < i) return false;
  const c = indiceMes(competencia);
  return indiceMes(i) <= c && c <= indiceMes(f);
}

// ── Resumo da empresa ─────────────────────────────────────────────

/**
 * Contagens da empresa. TotalPass: vagas não canceladas com 'totalpass' em
 * `adicionais` (com ou sem pessoa). Odonto e Psicólogo: só vagas elegíveis,
 * pelo controle em `controles` (chave = seatPublicId minúsculo); vaga elegível
 * sem controle no mapa conta pelo Hub (o efetivo sem linha do CRM).
 */
export function resumirAdicionais(
  vidas: readonly VidaHub[],
  controles: ReadonlyMap<string, Partial<Record<AdicionalVida, ControleAdicional>>>,
): ResumoAdicionais {
  const out: ResumoAdicionais = {
    totalpass: { vidas: 0 },
    clin_odonto: { contratadas: 0 },
    zenklub_psi: { contratadas: 0, ativos: 0 },
  };
  for (const v of vidas) {
    if (estaCancelada(v)) continue;
    if (hubMarca(v, 'totalpass')) out.totalpass.vidas += 1;
    if (!temPessoa(v)) continue;
    const doMapa = controles && typeof controles.get === 'function' ? controles.get(v.seatPublicId) : undefined;
    const status = (a: AdicionalVida): StatusAdicional =>
      (doMapa?.[a] ?? efetivoAdicional(v, a, null, null)).status;
    if (status('clin_odonto') !== 'nao_contratado') out.clin_odonto.contratadas += 1;
    const z = status('zenklub_psi');
    if (z !== 'nao_contratado') out.zenklub_psi.contratadas += 1;
    if (z === 'ativo') out.zenklub_psi.ativos += 1;
  }
  return out;
}
